import { Component, createRef } from "react";
import styles from "./jetpass-slot-selection.module.less";
import JetpassSlotListItem from "components/block-elements/jetpass-slot-list-item/jetpass-slot-list-item";
import GuestSelectionButtons from "components/block-elements/guest-selection-buttons/guest-selection-buttons";
import PropTypes from "prop-types";
import SiteConfigurationStore from "components/stores/site-configuration-store";
import MyPageLoader from "components/block-elements/my-page-loader/my-page-loader";
import { GhostButton, Modal, PrimaryButton, SecondaryButton, isArray } from "@parksandresorts/core";
import animateScrollTo from "animated-scroll-to";
import BackButton from "components/block-elements/back-button/back-button";
import { motion } from "polyfill/motion-polyfill/motion-polyfill";
import { MANDATORY_PASSES_STEP } from "components/block-elements/jetpass-selection/constants/jetpass-constants";

export default class JetpassSlotSelection extends Component {
  constructor(props) {
    super(props);

    this.headline =
      this.props.stepType === MANDATORY_PASSES_STEP
        ? SiteConfigurationStore.getLabel("JETPASS_BOOK_JETPASS_MANDATORY", "Boka Entré")
        : SiteConfigurationStore.getLabel("JETPASS_BOOK_JETPASS", "Boka JetPass");
    this.ofText = SiteConfigurationStore.getLabel("OF", "av");
    this.alreadySelectedPassesHeadline = SiteConfigurationStore.getLabel(
      "JETPASS_ALREADY_SELECTED_JETPASSES",
      "Nuvarande JetPass",
    );
    this.jetPassSelectedText = SiteConfigurationStore.getLabel("JETPASS_JETPASS_SELECTED", "JetPass valda");
    this.completeButtonText = SiteConfigurationStore.getLabel("JETPASS_COMPLETE_BOOKING_TEXT", "Boka JetPass!");
    this.optOutContinueButtonText = SiteConfigurationStore.getLabel(
      "JETPASS_CONTINUE_WITHOUT_BUTTON",
      "Fortsätt utan att välja fler pass",
    );
    this.bookOnlySelectedLabel = SiteConfigurationStore.getLabel(
      "JETPASS_BOOK_ONLY_SELECTED",
      "Boka bara valda JetPass",
    );
    this.selectMoreLabel = SiteConfigurationStore.getLabel("JETPASS_SELECT_MORE", "Välj fler JetPass");
    this.confirmNotSelectAllLabel = SiteConfigurationStore.getLabel(
      "JETPASS_CONFIRM_NOT_SELECT_ALL",
      "Är du säker på att du inte vill välja alla JetPass som ingår i din bokning?",
    );
    this.youHaveOnlySelectedLabel = SiteConfigurationStore.getLabel(
      "JETPASS_YOU_HAVE_ONLY_SELECTED",
      "Du har bara valt",
    );
    this.availableJetPass = SiteConfigurationStore.getLabel("JETPASS_AVAILABLE_JETPASS", "tillgängliga JetPass");
    this.clarificationLabel = SiteConfigurationStore.getLabel(
      "JETPASS_CLARIFICATION",
      "Utöver ditt Jetpass har du självklart fria åk i våra attraktioner (halva dagen i tivolit och hela dagen i resten av parken)!",
    );
    this.loadingLabel = SiteConfigurationStore.getLabel("LOADING", "Laddar");
    this.quantityLabel = SiteConfigurationStore.getLabel(
      "JETPASS_AVAILABLE_TIMESLOTS_FOR_QUANTITY",
      "Tillgängliga tider för %number% st biljetter",
    );
    this.quantityLabel = this.quantityLabel.replace(/%number%/gi, this.props.productQuantity);
    this.selectionCompleteVariable =
      this.props.stepType === MANDATORY_PASSES_STEP ? "entryPassSelectionComplete" : "jetpassSelectionComplete";
    this.passesArrayVariable = this.props.stepType === MANDATORY_PASSES_STEP ? "entryPasses" : "jetPasses";
    this.totalPassesVariable =
      this.props.stepType === MANDATORY_PASSES_STEP ? "numberOfEntryTickets" : "numberOfJetpasses";

    this.scrollRef = this.props.scrollRef;
    this.focusRef = createRef();
    this.overlayTriggerRef = createRef();

    this.state = {
      showContinueOverlay: false,
    };
  }

  toggleOverLay() {
    this.setState({ showContinueOverlay: !this.state.showContinueOverlay });

    setTimeout(() => {
      if (!this.state.showContinueOverlay) {
        this.overlayTriggerRef?.current?.focus();
      }
    }, 0);
  }

  scrollAndSelectActiveGuest(indexForNextGuestWithSelectionsLeft) {
    animateScrollTo(0, { elementToScroll: this.scrollRef?.current }).then((pos) => {
      this.props.selectActiveGuest(indexForNextGuestWithSelectionsLeft);
    });
  }

  getSelectionDataWithoutAlreadySelected(selectionData) {
    if (!isArray(this.props.alreadySelectedPasses)) {
      return selectionData;
    }

    if (!isArray(selectionData)) {
      return [];
    }

    const selectedAttractionIds = this.props.alreadySelectedPasses.reduce((result, pass) => {
      if (!pass.beingEdited) {
        return [...result, pass.attractionId];
      }
      return result;
    }, []);

    const withoutSelected = selectionData.filter(
      (attraction) => !selectedAttractionIds.includes(attraction.attractionId),
    );

    return withoutSelected;
  }

  getGuestBookingsForAttraction(attractionId) {
    if (!isArray(this.props.guests)) {
      return new Map();
    }

    const timeslotsMap = new Map();

    for (const guest of this.props.guests) {
      for (const pass of guest[this.passesArrayVariable]) {
        if (pass.attractionId === attractionId) {
          const passKey = pass.startDateTime + "-" + pass.endDateTime;
          const bookingsOnTimeslot = timeslotsMap.get(passKey);
          const guestObject = {
            name: guest.name,
            color: guest.color,
          };

          if (!isArray(bookingsOnTimeslot)) {
            const bookingsOnTimeslot = [guestObject];
            timeslotsMap.set(passKey, bookingsOnTimeslot);
          } else {
            bookingsOnTimeslot.push(guestObject);
          }
        }
      }
    }

    return timeslotsMap;
  }

  getOtherBookedTimesSlotsForGuest(attractionId) {
    if (!isArray(this.props.activeGuest[this.passesArrayVariable])) {
      return [];
    }

    const passesToReturn = [];

    for (const selectedPass of this.props.activeGuest[this.passesArrayVariable]) {
      if (selectedPass.attractionId !== attractionId) {
        passesToReturn.push({ timeSlotBegin: selectedPass.startDateTime, attractionName: selectedPass.attractionName });
      }
    }

    return passesToReturn;
  }

  getTimeSlotsWithModifiedQuantity(timeslots, guestBookings) {
    if (!isArray(timeslots)) {
      return [];
    }

    const timeSlotsWithAdjustedQuantity = timeslots.map((timeslot) => {
      const timeslotIdentifier = timeslot.timeSlotBegin + "-" + timeslot.timeSlotEnd;
      const otherGuestBookings = guestBookings.get(timeslotIdentifier);
      const otherGuestBookingQuantity = otherGuestBookings ? otherGuestBookings.length : 0;
      const modifiedQuantity = timeslot.quantity - otherGuestBookingQuantity;

      return {
        ...timeslot,
        modifiedQuantity: modifiedQuantity,
      };
    });

    return timeSlotsWithAdjustedQuantity;
  }

  getNextGuestWithSelectionsLeft(enableGuestsBeforeCurrentIndex = false) {
    if (!isArray(this.props.guests)) {
      return null;
    }

    if (enableGuestsBeforeCurrentIndex) {
      for (let i = 0; i < this.props.guests.length; i++) {
        if (!this.props.guests[i][this.selectionCompleteVariable]) {
          return i;
        }
      }

      return -1;
    }

    for (let i = 0; i < this.props.guests.length; i++) {
      if (i > this.props.multiSelectionGuestIndex && !this.props.guests[i][this.selectionCompleteVariable]) {
        return i;
      }
    }

    return -1;
  }

  renderAlreadySelectedPasses(alreadySelectedPasses) {
    if (!isArray(alreadySelectedPasses)) {
      return null;
    }

    return (
      <>
        <h5 className={styles.alreadySelectedHeading}>{this.alreadySelectedPassesHeadline}</h5>
        <ul className={styles.alreadySelectedList}>
          {alreadySelectedPasses.map((selectedPass) => (
            <li key={selectedPass.attractionId} className={styles.alreadySelectPassWrapper}>
              <h5 className={styles.alreadySelectedPassName}>{selectedPass.name}</h5>
              <p className={styles.alreadySelectedPassTime}>{selectedPass.time}</p>
            </li>
          ))}
        </ul>
      </>
    );
  }

  renderSingleSelectionButtons() {
    return this.props.stepType === MANDATORY_PASSES_STEP
      ? this.renderSinglebuttonsForMandatory()
      : this.renderSingleButtonsForJetpass();
  }

  renderSingleButtonsForJetpass() {
    return this.props.selectionComplete ? (
      <div className={`${styles.bottomButtonWrapper} fade-in-delay-700`}>
        <PrimaryButton onClick={this.props.optOut} label={this.completeButtonText} className={styles.completeButton} />
      </div>
    ) : (
      <div ref={this.overlayTriggerRef} tabIndex={-1} className={`${styles.bottomButtonWrapper} fade-in-delay`}>
        <GhostButton
          label={this.optOutContinueButtonText}
          onClick={() => this.toggleOverLay()}
          className={styles.optOutButton}
        />
      </div>
    );
  }

  renderSinglebuttonsForMandatory() {
    return this.props.selectionComplete ? (
      <div className={`${styles.bottomButtonWrapper} fade-in-delay-700`}>
        <PrimaryButton
          onClick={this.props.isLastStep ? this.props.optOut : this.props.nextStep}
          label={this.props.isLastStep ? "Slutför bokning" : "Nästa steg"}
          className={styles.completeButton}
        />
      </div>
    ) : null;
  }

  renderMultiSelectionButtonsForMandatory() {
    if (this.props.allSelectionComplete) {
      return (
        <div className={`${styles.bottomButtonWrapper} fade-in-delay-700`}>
          <PrimaryButton
            onClick={this.props.isLastStep ? this.props.optOut : this.props.nextStep}
            label={this.props.isLastStep ? "Slutför bokning" : "Nästa steg"}
            className={styles.completeButton}
          />
        </div>
      );
    }

    const selectionComplete = this.props.activeGuest[this.selectionCompleteVariable];
    const indexForGuestWithSelectionsLeft = this.getNextGuestWithSelectionsLeft(true);

    if (selectionComplete && indexForGuestWithSelectionsLeft >= 0) {
      return (
        <div className={`${styles.bottomButtonWrapper} fade-in-delay-700`}>
          <PrimaryButton
            onClick={() => this.scrollAndSelectActiveGuest(indexForGuestWithSelectionsLeft)}
            label="Nästa gäst"
            className={styles.completeButton}
          />
        </div>
      );
    }

    return null;
  }

  renderMultiSelectionButtonsForJetpass() {
    if (this.props.allSelectionComplete) {
      return (
        <div className={`${styles.bottomButtonWrapper} fade-in-delay-700`}>
          <PrimaryButton onClick={this.props.optOut} label="Slutför bokning" className={styles.completeButton} />
        </div>
      );
    }

    if (this.props.multiSelectionGuestIndex === this.props.guests.length - 1) {
      return (
        <div ref={this.overlayTriggerRef} tabIndex={-1} className={`${styles.bottomButtonWrapper} fade-in-delay`}>
          <GhostButton
            label="Slutför utan att välja alla tider"
            onClick={() => this.toggleOverLay()}
            className={styles.optOutButton}
          />
        </div>
      );
    }

    const selectionComplete = this.props.activeGuest[this.selectionCompleteVariable];
    const indexForNextGuestWithSelectionsLeft = this.getNextGuestWithSelectionsLeft();
    if (selectionComplete && indexForNextGuestWithSelectionsLeft >= 0) {
      return (
        <div className={`${styles.bottomButtonWrapper} fade-in-delay-700`}>
          <PrimaryButton
            onClick={() => this.scrollAndSelectActiveGuest(indexForNextGuestWithSelectionsLeft)}
            label="Nästa gäst"
            className={styles.completeButton}
          />
        </div>
      );
    }

    if (indexForNextGuestWithSelectionsLeft === -1) {
      return (
        <div ref={this.overlayTriggerRef} tabIndex={-1} className={`${styles.bottomButtonWrapper} fade-in-delay`}>
          <GhostButton
            label="Slutför utan att välja alla tider"
            onClick={() => this.toggleOverLay()}
            className={styles.optOutButton}
          />
        </div>
      );
    }

    return (
      <div className={`${styles.bottomButtonWrapper} fade-in-delay`}>
        <GhostButton
          label="Nästa gäst utan att välja tid"
          onClick={
            indexForNextGuestWithSelectionsLeft > 0
              ? () => this.scrollAndSelectActiveGuest(indexForNextGuestWithSelectionsLeft)
              : null
          }
          className={styles.optOutButton}
        />
      </div>
    );
  }

  renderMultiSelectionButtons() {
    return this.props.stepType === MANDATORY_PASSES_STEP
      ? this.renderMultiSelectionButtonsForMandatory()
      : this.renderMultiSelectionButtonsForJetpass();
  }

  render() {
    const selectionDataWithoutAlreadySelected = this.getSelectionDataWithoutAlreadySelected(this.props.selectionData);
    const oneOption =
      selectionDataWithoutAlreadySelected instanceof Array && selectionDataWithoutAlreadySelected.length === 1;
    return (
      <div>
        <div
          className={`${styles.wrapper} ${
            this.state.showContinueOverlay ? styles.scrollLock : ""
          } block--max-width-narrow`}
          ref={this.focusRef}
        >
          {this.props.onBack && this.props.enableBackButton && <BackButton onClick={this.props.onBack} />}
          <h2 className={`${styles.headline} h3`}>
            {this.props.customHeading ? this.props.customHeading : this.headline}
          </h2>
          {selectionDataWithoutAlreadySelected ? (
            <>
              {isArray(this.props.guests) && this.props.guests.length > 1 && !this.props.lockMultiSelectionToggle && (
                <SecondaryButton
                  complementary
                  className={styles.toggleMultiSelectButton}
                  onClick={this.props.toggleMultiSelection}
                  label={this.props.multiSelection ? "Boka gemensamma tider" : "Boka individuella tider"}
                />
              )}
              {this.props.multiSelection && isArray(this.props.guests) && (
                <GuestSelectionButtons
                  activeGuestIndex={this.props.multiSelectionGuestIndex}
                  guests={this.props.guests}
                  passesArrayVariable={this.passesArrayVariable}
                  totalPassesVariable={this.totalPassesVariable}
                  onSelectActiveGuest={this.props.selectActiveGuest}
                />
              )}
              {this.clarificationLabel && (
                <div className={styles.clarificationWrapper}>
                  <p className={[styles.clarificationText, "p_small"].join(" ")}>{this.clarificationLabel}</p>
                </div>
              )}
              {isArray(this.props.alreadySelectedPasses) &&
                this.renderAlreadySelectedPasses(this.props.alreadySelectedPasses)}
              {this.props.activeGuest && (
                <div className={styles.numberOfPassesWrapper}>
                  <h5 className={styles.numberOfPassesText}>
                    {this.props.multiSelection
                      ? `Välj tid åt ${this.props.activeGuest.name}`
                      : this.props.productQuantity > 1
                      ? "Välj tid för alla"
                      : "Välj tider"}
                    <span className={styles.numberOfPasses}>
                      ({this.props.activeGuest[this.passesArrayVariable].length}/
                      {this.props.activeGuest[this.totalPassesVariable]})
                    </span>
                  </h5>
                </div>
              )}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className={styles.selectionListWrapper}
              >
                <ul className={styles.selectionList}>
                  {selectionDataWithoutAlreadySelected.map((item) => {
                    const guestBookingsForAttraction = this.getGuestBookingsForAttraction(item.attractionId);
                    const timeSlotsWithModifiedQuantity = this.getTimeSlotsWithModifiedQuantity(
                      item.timeSlots,
                      guestBookingsForAttraction,
                    );
                    const otherBookedTimesSlotsForGuest = this.getOtherBookedTimesSlotsForGuest(item.attractionId);

                    const updatedItem = {
                      ...item,
                      timeSlots: timeSlotsWithModifiedQuantity,
                    };
                    return (
                      <li key={item.attractionId} className={styles.selectionListItem}>
                        <JetpassSlotListItem
                          stepType={this.props.stepType}
                          productTimeLimit={this.props.productTimeLimit}
                          disabledTimeSlots={otherBookedTimesSlotsForGuest} //you can't book same timeslot you already chose on other attractions
                          selectTimeSlot={this.props.selectTimeSlot}
                          removeTimeSlot={this.props.removeTimeSlot}
                          multiSelection={this.props.multiSelection}
                          selectedEntryTime={this.props.selectedEntryTime}
                          selectionComplete={this.props.selectionComplete}
                          expanded={oneOption ? true : false}
                          numberOfGuests={this.props.numberOfGuests}
                          guestBookingsForAttraction={guestBookingsForAttraction}
                          {...updatedItem}
                        />
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
              {this.props.multiSelection ? this.renderMultiSelectionButtons() : this.renderSingleSelectionButtons()}
            </>
          ) : (
            <div className={styles.loader}>
              <h2>{this.loadingLabel}</h2>
              <MyPageLoader />
            </div>
          )}
        </div>
        {this.state.showContinueOverlay && (
          <Modal isOpen={true} onClose={() => this.toggleOverLay()} refWrapperElement={this.focusRef?.current}>
            <div className={`${styles.overlay} fade-in`}>
              <div className={`${styles.contentWrapper}`}>
                <div className={styles.textWrapper}>
                  <h4
                    className={styles.overlayHeadline}
                  >{`${this.youHaveOnlySelectedLabel} ${this.props.numberOfTotalSelectedPasses} ${this.ofText} ${this.props.numberOfTotalPasses} ${this.availableJetPass}`}</h4>
                  <p className={styles.overlayParagraph}>{this.confirmNotSelectAllLabel}</p>
                </div>
                <div className={styles.overlayButtonWrapper}>
                  <PrimaryButton
                    label={this.selectMoreLabel}
                    onClick={() => this.toggleOverLay()}
                    className={styles.overlayBackButton}
                  />
                  <SecondaryButton
                    label={this.bookOnlySelectedLabel}
                    onClick={this.props.optOut}
                    className={styles.overlayContinueButton}
                  />
                </div>
              </div>
            </div>
          </Modal>
        )}
      </div>
    );
  }
}

JetpassSlotSelection.propTypes = {
  selectionData: PropTypes.array,
  selectedTimeSlots: PropTypes.array,
  numberOfTotalSelectedPasses: PropTypes.number,
  numberOfTotalPasses: PropTypes.number,
  selectionComplete: PropTypes.bool,
  multiSelectionGuestIndex: PropTypes.number,
  activeGuest: PropTypes.object,
  guests: PropTypes.array,
  selectActiveGuest: PropTypes.func,
  multiSelection: PropTypes.bool,
  toggleMultiSelection: PropTypes.func,
};
